// ============================================================
// YOM Intent — Google Product Taxonomy (docs/intent.md §5)
//
// The interest spine: every scored signal lands on a GPT node id (0 =
// unknown). Platforms put the id or path on their events, or push a mapping
// of their own category strings into the project config; YOM holds no
// platform's mapping. Sensitive subtrees (decision D6) are never scored or
// exported.
// ============================================================
import { GPT_DATA, GPT_NODE_COUNT, GPT_VERSION } from './gpt-taxonomy.data.js';
export { GPT_NODE_COUNT, GPT_VERSION };
/** Category of a signal no mapping could place. */
export const UNKNOWN_CATEGORY = 0;
let index = null;
/** Parsed on first use (≈ 5,600 nodes), then kept for the isolate's life. */
function idx() {
    if (index) {
        return index;
    }
    const byId = new Map();
    const children = new Map();
    for (const line of GPT_DATA.split('\n')) {
        const [id, parent, name] = line.split('|');
        const node = { id: Number(id), parent: Number(parent), name };
        byId.set(node.id, node);
        const siblings = children.get(node.parent);
        if (siblings) {
            siblings.push(node.id);
        }
        else {
            children.set(node.parent, [node.id]);
        }
    }
    const byPath = new Map();
    for (const node of byId.values()) {
        byPath.set(gptPathOf(byId, node.id).toLowerCase(), node.id);
    }
    index = { byId, byPath, children };
    return index;
}
function gptPathOf(byId, id) {
    const names = [];
    for (let n = byId.get(id); n; n = n.parent ? byId.get(n.parent) : undefined) {
        names.unshift(n.name);
    }
    return names.join(' > ');
}
export function gptNode(id) {
    return idx().byId.get(id) ?? null;
}
export function isGptId(id) {
    return typeof id === 'number' && Number.isInteger(id) && idx().byId.has(id);
}
/** "Furniture > Sofas"; '' for unknown ids. */
export function gptPath(id) {
    return idx().byId.has(id) ? gptPathOf(idx().byId, id) : '';
}
/** The node and its ancestors, most specific first. */
export function gptLineage(id) {
    const out = [];
    for (let n = idx().byId.get(id); n; n = n.parent ? idx().byId.get(n.parent) : undefined) {
        out.push(n.id);
    }
    return out;
}
/** Top-level node (Furniture, Home & Garden …) of an id; 0 for unknown. */
export function gptTop(id) {
    const line = gptLineage(id);
    return line.length ? line[line.length - 1] : UNKNOWN_CATEGORY;
}
/** The node and every descendant. */
export function gptSubtree(id) {
    const out = [];
    const stack = [id];
    while (stack.length) {
        const n = stack.pop();
        if (idx().byId.has(n)) {
            out.push(n);
        }
        stack.push(...(idx().children.get(n) ?? []));
    }
    return out;
}
/**
 * A GPT id (number or digits) or full path ("Furniture > Sofas", case-insensitive) → node id;
 * null when it names no node.
 */
export function resolveGptCategory(value) {
    if (typeof value === 'number') {
        return isGptId(value) ? value : null;
    }
    if (typeof value !== 'string') {
        return null;
    }
    const v = value.trim();
    if (/^\d{1,7}$/.test(v)) {
        return isGptId(Number(v)) ? Number(v) : null;
    }
    const path = v
        .split(/\s*>\s*/)
        .join(' > ')
        .toLowerCase();
    return idx().byPath.get(path) ?? null;
}
/** Name search for building segments: matches on any word of the path. */
export function searchGpt(q, limit = 20) {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return [];
    }
    const out = [];
    for (const [path, id] of idx().byPath) {
        if (words.every(w => path.includes(w))) {
            out.push({ id, path: gptPath(id), depth: path.split(' > ').length });
        }
    }
    return out
        .sort((a, b) => a.depth - b.depth || a.path.length - b.path.length || a.path.localeCompare(b.path))
        .slice(0, limit)
        .map(({ id, path }) => ({ id, path }));
}
/**
 * Sensitive subtrees (decision D6: health, religion, sexuality; pregnancy and post-partum
 * care count as health). Never scored, never exported. Projects may add nodes, never remove
 * these.
 */
export const SENSITIVE_GPT_ROOTS = [
    { id: 491, why: 'health' }, // Health & Beauty > Health Care
    { id: 2496, why: 'health' }, // Business & Industrial > Medical
    { id: 502987, why: 'health' }, // Apparel & Accessories > … > Maternity Belts & Support Bands
    { id: 565, why: 'health' }, // Baby & Toddler > Nursing & Feeding > Breast Pumps
    { id: 505366, why: 'health' }, // … > Breast Pump Accessories
    { id: 7234, why: 'health' }, // … > Breast Milk Storage Containers
    { id: 772, why: 'sexuality' }, // Mature (Erotic, Weapons)
    { id: 5605, why: 'religion' }, // Religious & Ceremonial
    { id: 5483, why: 'religion' }, // Apparel & Accessories > … > Religious Ceremonial Clothing
];
const SENSITIVE_IDS = new Set(SENSITIVE_GPT_ROOTS.map(r => r.id));
/** True when the node or any ancestor is sensitive (built-in or the project's `extra`). */
export function isSensitiveCategory(id, extra = []) {
    if (id === UNKNOWN_CATEGORY) {
        return false;
    }
    return gptLineage(id).some(n => SENSITIVE_IDS.has(n) || extra.includes(n));
}
//# sourceMappingURL=taxonomy.js.map